import { inject, provide } from 'vue';
import type { JobSearchOptions, Locality } from '@/types';

type JobContext = {
    options: JobSearchOptions;
    getLabel: (key: string, value: string) => string;
};

export default function useJobContext() {
    const injectContext = () => {
        const context = inject('jobContext');
        return context as JobContext;
    };

    const provideContext = (
        localities: Record<string, Locality>,
        locationTypeOptions: Record<string, string>
    ) => {
        const localityOptions: Record<string, string> = Object.fromEntries(
            Object.entries(localities).map(([id, properties]) => [
                id,
                properties.locality,
            ])
        );

        const options: JobSearchOptions = {
            type: {
                name: 'Nach Stellentyp',
                children: {
                    '1': 'Berufstätige',
                    '2': 'Ausbildung',
                    '4': 'Studium',
                },
            },
            jobTitle: {
                name: 'Nach Berufsbezeichnung',
                children: {
                    Justizangestellte: 'Justizangestellte/-r',
                    Justizfachwirt: 'Justizfachwirt/-in',
                    Rechtspfleger: 'Rechtspfleger/-in',
                    Richter: 'Richter/-in',
                    Justizwachtmeister: 'Justizwachtmeister/-in',
                    Staatsanwalt: 'Staatsanwalt/-anwältin',
                    Amtsanwalt: 'Amtsanwalt/-anwältin',
                    Gerichtsvollzieher: 'Gerichtsvollzieher/-in',
                    Referendar: 'Referendar/-in',
                    Justizsozialarbeiter:
                        'Justizsozialarbeiterin / Justizsozialarbeiter/-in',
                    Verwaltungsmitarbeiter: 'Verwaltungsmitarbeiter/-in (AJSD)',
                    'Fachinformatiker Systemintegration':
                        'Fachinformatiker/-in für Systemintegration',
                    'Fachinformatiker Anwendungsentwicklung':
                        'Fachinformatiker/-in für Anwendungsentwicklung',
                    Justizassistenz: 'Justizassistenz',
                },
            },
            location: {
                name: 'Nach Standort',
                children: localityOptions,
            },
            locationType: {
                name: 'Nach Justizeinrichtung',
                children: locationTypeOptions,
            },
        };

        const getLabel = (key: string, value: string) => {
            return options?.[key]?.children[value] ?? value;
        };

        provide('jobContext', {
            options,
            getLabel,
        } as JobContext);
    };

    return [injectContext, provideContext] as const;
}
